import React, { useEffect, useState, lazy } from 'react';
import { useTranslation } from 'react-i18next';
import RenderComponent from './RenderComponent';
import PropTypes from 'prop-types';
import moment from 'moment';
import * as _ from 'lodash';
import { hasValue, getLogsInfoDisplay, hasEnabledFeature, isDriver } from 'globalutils/fmUtils';
import { pageUrls } from 'globalconfig/constants/keys';
import { baseYData, xData, schema, dataFormMappings } from './config';
import { compareObjectArrays, isUdEdit } from 'globalutils/commonUtils';
import {
  getCoDriverEventReassignment,
  hasDriverChangeOnEligibleEntries,
  hasActiveReassignmentRequests,
  isWholeLogEditBlocked,
} from './coDriverEventReassignmentUtils';
import { hasActiveUdReassignmentRequests, isWholeLogEditBlockedByUd } from './udReassignmentUtils';
import { getPendingReassignmentMessage } from './reassignmentPendingMessage';

const layout = window?.$environment?.CURRENT_LAYOUT;
const Label = lazy(() => import(`globalcomponents/UI/Label/${layout}`));
const Form = lazy(() => import(`globalcomponents/UI/Form/${layout}`));
const Message = lazy(() => import(`globalcomponents/UI/Message/${layout}`));

const Component = ({
  dailyLogDetails,
  saveDailyLogDetails,
  createUdReassignment,
  apiStatus,
  id,
  clearUpdateDailyLogDetails,
  setDailyLogEntries,
  history,
  viewOnly,
  Styled,
  udEdit,
  getDailyLogDetails,
  createCoDriverEventReassignment,
}) => {
  const fetchDailyLogDetailsApiId = 'fetchDailyLogDetails';
  const { i18n } = useTranslation();
  const [finalListForUpdate, setFinalListForUpdate] = useState([]);
  const [providerMode, setProviderMode] = useState(false);
  const [validationMessage, setValidationMessage] = useState('');
  const [yData, setYData] = useState([]);
  const [assignedDriver, setAssignedDriver] = useState({});
  const [drivers, setDrivers] = useState([]);

  useEffect(() => {
    const driversList = Object.keys(dailyLogDetails.originalData.dailyLog.companyDrivers || {}).map((key) => {
      const item = dailyLogDetails.originalData.dailyLog.companyDrivers[key];
      return {
        label: item,
        value: key,
      };
    });

    setDrivers(driversList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dailyLogDetails]);

  useEffect(() => {
    const enableEditOW = hasEnabledFeature('editOilFieldWaitingEnabled') && dailyLogDetails?.originalData?.enableEditOW === true;
    
    let yDataCopy = [...baseYData];
    if (viewOnly || enableEditOW) {
      if (!yDataCopy.some(item => item.value === 'OW')) {
        yDataCopy.push({ value: 'OW', label: 'OIL_FIELD_WAITING' });
      }
    }

    setYData(yDataCopy);

    const object = dailyLogDetails?.originalData?.dailyLog;

    const getDriverIdFromName = (list) => {
      for (const key in list) {
        if (list[key] === 'Unidentified Driver') return key;
      }
    };

    setAssignedDriver(
      udEdit
        ? {
            value: getDriverIdFromName(dailyLogDetails.originalData.dailyLog.companyDrivers),
            label: 'Unidentified Driver',
          }
        : {
            label: object?.driverName,
            value: object?.driverId,
          }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dailyLogDetails?.originalData]);

  const hasFetchDailyLogDetails = hasValue(apiStatus.status, 'api', fetchDailyLogDetailsApiId);
  const saveDailyLogDetailsApiId = 'saveDailyLogDetails';
  const hasUpdateDailyLogDetails = hasValue(apiStatus.status, 'api', saveDailyLogDetailsApiId);

  const coDriverEventReassignment = getCoDriverEventReassignment(dailyLogDetails.originalData);
  const udReassignment = dailyLogDetails?.originalData?.udReassignment;
  const originalEntries = dailyLogDetails.originalData?.dailyLog?.dailyLogEntries || [];
  const currentEntries = dailyLogDetails.finalData?.dailyLogEntries || [];
  const pendingDriverChange = hasDriverChangeOnEligibleEntries(
    originalEntries,
    currentEntries,
    coDriverEventReassignment
  );
  const wholeLogEditBlocked =
    isWholeLogEditBlocked(coDriverEventReassignment, pendingDriverChange) ||
    isWholeLogEditBlockedByUd(udReassignment);
  const tableEditBlocked = viewOnly || wholeLogEditBlocked;

  const updateFinalList = (source) => {
    const newList = dailyLogDetails.finalData.dailyLogEntries.map((item) => {
      const parentItem = source.filter((sourceItem) => {
        return sourceItem.key === item.id;
      });
      const { key, ...updatedItem } = parentItem[0] || {};
      return parentItem[0] ? { ...updatedItem } : item;
    });
    return newList;
  };

  const checkIfEdited = (source) => {
    let temp = [];
    for (let index = 0; index < source.length; index++) {
      let point = source[index];
      point.isLogEntryEdited = false;
      const isEdited = point.time.includes('edited') > 0;
      if (isEdited) {
        point.isLogEntryEdited = true;
        const time = point.time.replace('edited', '');
        point = {
          ...point,
          time: time,
        };
        temp.push(point);
      } else if (point.status.edited) {
        point.isLogEntryEdited = true;
        point = {
          ...point,
          status: { value: point.status.value, label: point.status.label },
        };
        temp.push(point);
      } else if (point.deleted) {
        point.isLogEntryEdited = true;
        if (source[index + 1]?.deleted) {
          if (index === 0) {
            temp[0] = {
              ...source[2],
              x: point.x,
              time: point.time,
            };
            temp[index + 1] = {
              ...source[index ? index : 3],
            };
          } else if (index === source.length - 2) {
            temp[index - 1] = {
              ...temp[index - 1],
              x: source[index + 1].x,
              time: source[index + 1].time,
            };
          } else {
            temp[index - 1] = {
              ...temp[index - 1],
              x: source[index + 2].x,
              time: source[index + 2].time,
            };
          }
        }
      } else {
        temp.push(point);
      }
    }
    return temp;
  };

  const getTranslatedValue = (value) => {
    const data = ['OFF_DUTY', 'SLEEPER_BERTH', 'DRIVING', 'ON_DUTY', 'OIL_FIELD_WAITING'];
    let finalVal = '';
    if (!data.includes(value)) {
      data.forEach((ele) => {
        if (i18n.t(ele) == value) finalVal = ele;
      });
      return finalVal;
    } else {
      return value;
    }
  };

  const resetDataList = (source) => {
    let result = [];
    const getMinuteFromTime = (time, index) => {
      const hr = time.split(':')[1].includes('am')
        ? parseInt(time.split(':')[0], 10) === 12
          ? 0
          : time.split(':')[0]
        : parseInt(time.split(':')[0], 10) === 12
        ? 12
        : parseInt(time.split(':')[0], 10) + 12;
      const min = parseInt(time.split(':')[1].slice(0, -2), 10);
      return time === '12:00am' && index !== 0 ? 1440 : hr * 60 + min;
    };
    let linearId = 0;
    for (let i = 0; i < source.length; i++) {
      const element = source[i];
      if (i % 2 === 0) {
        delete element.noEdit;
        result.push({
          annotation: '',
          coDriverId: '',
          driverFullName: '',
          eldData: false,
          id: udEdit ? element.key : linearId,
          isAOBRDEdit: false,
          unIdentifiedLogStatus: udEdit
            ? dailyLogDetails.originalData.dailyLog.companyDrivers[parseInt(element?.driverId?.value, 10)] ===
              'Unidentified Driver'
              ? 'ASSIGNED'
              : 'UNASSIGNED'
            : 'UNASSIGNED',
          status: getTranslatedValue(element.status.label),
          note: element.note || '',
          stateModifier: element?.stateModifier?.value !== 'please' ? element?.stateModifier?.value || '' : '',
          oilFieldWaiting: element?.stateModifier !== '' && element?.stateModifier?.value !== 'please' ? true : false,
          location: element.location || '',
          driverId: parseInt(element?.driverId?.value, 10) || 0,
          time_from: element.time,
          time_to: source[i + 1].time,
          readOnly: element.readOnly,
          key: element.key,
          minutes_from: getMinuteFromTime(element.time, i),
          minutes_to: getMinuteFromTime(source[i + 1].time, i),
          isLogEntryEdited: element?.isLogEntryEdited || source[i + 1]?.isLogEntryEdited,
        });
        linearId++;
      }
    }

    return result;
  };

  const getData = (fields, logsEntries) => {
    const dataForm = dataFormMappings.map(({ name, field, log }) => ({
      name,
      value: fields[field] || dailyLogDetails.originalData.dailyLog[log],
    }));

    const temp = udEdit ? updateFinalList(logsEntries) : logsEntries;

    const dataTable = temp.map((u) => ({
      minutes_from: u.minutes_from,
      stateModifier: u.stateModifier,
      location: u.location,
      driverId: u.driverId,
      note: u.note,
    }));

    const initialData = (dailyLogDetails?.finalData?.dailyLogEntries || []).map((u) => ({
      minutes_from: u.minutes_from,
      stateModifier: u.stateModifier,
      location: u.location,
      driverId: u.driverId,
      note: u.note,
    }));

    const differenceData = _.differenceWith(initialData, dataTable, _.isEqual);

    if (differenceData.length) {
      differenceData.forEach((o) => {
        const idx = temp.findIndex((item) => item.minutes_from === o.minutes_from);
        if (idx > -1) temp[idx].isLogEntryEdited = true;
      });
    }

    let editedEvents = [];
    const initialLogs = dailyLogDetails.originalData.dailyLog.dailyLogEntries || [];

    temp.forEach((ele) => {
      ele.editedEntryId = [];
      editedEvents.forEach((item) => {
        if ((ele.minutes_from >= item.minutes_from && ele.minutes_to <= item.minutes_to) || ele.key == item.id) {
          ele.editedEntryId.push(item.eventReference);
        }
      });
    });
    const { changedOriginalObjects } = compareObjectArrays(initialLogs, temp, ["time_from", "time_to", "status", "note", "stateModifier", "location", "driverId"]);
    return {
      dataTbl: { ...temp },
      dataForm,
      editedEvents: changedOriginalObjects,
      driverName: dailyLogDetails.originalData.dailyLog.driverName,
      idPub: id,
      comment: fields.comment,
    };
  };

  const handleDocument = (logsEntries) => {
    const elements = document.getElementsByClassName('time_wrapper');

    for (let el of elements) {
      el.classList.remove('dailyLogDateError');
    }

    for (let i = 0; i < logsEntries.length - 1; i++) {
      const currentTime = moment(logsEntries[i].time_from.replace('edited', ''), 'h:mma');
      const nextTime = moment(logsEntries[i + 1].time_from.replace('edited', ''), 'h:mma');

      if (!udEdit && nextTime.isBefore(currentTime)) {
        const v = document.getElementById(`timeToDailyLog${i + i + 1}`);
        if (v) v.classList.add('dailyLogDateError');
        return false;
      }
    }
  };

  const handlSubmit = (fields) => {
    if (wholeLogEditBlocked) {
      setValidationMessage(
        pendingDriverChange
          ? i18n.t('logs.reassignDriverChangeBlocked')
          : hasActiveReassignmentRequests(coDriverEventReassignment)
            ? getPendingReassignmentMessage({
                type: 'codriver',
                meta: coDriverEventReassignment,
                entries: originalEntries,
                i18n,
                fallbackKey: 'logs.reassignPendingRequest',
              })
            : hasActiveUdReassignmentRequests(udReassignment)
              ? getPendingReassignmentMessage({
                  type: 'ud',
                  meta: udReassignment,
                  entries: originalEntries,
                  i18n,
                  fallbackKey: 'logs.udReassignEditDisabled',
                })
              : isWholeLogEditBlockedByUd(udReassignment)
                ? udReassignment?.disabledReason || i18n.t('logs.udReassignEditDisabled')
                : coDriverEventReassignment?.disabledReason || i18n.t('logs.reassignPendingRequest')
      );
      return;
    }

    if (isUdEdit) {
      const isValid = finalListForUpdate.every((item) => item.driverId.value !== 0 && item.driverId.value !== null);
      if (!isValid) {
        setValidationMessage(i18n.t('Please fill in atleast one assigned driver.'));
        return;
      }
    }

    const logsEntries = resetDataList([...checkIfEdited(finalListForUpdate)]);
    const finalData = getData(fields, logsEntries);

    handleDocument(logsEntries);

    const errorClass = document.getElementsByClassName('dailyLogDateError');
    if (errorClass?.length === 0) {
      saveDailyLogDetails({
        apiId: saveDailyLogDetailsApiId,
        data: { data: finalData, id: id },
        udEdit: udEdit,
        providerMode: providerMode,
        callback: () => {
          history.push(pageUrls.logsList);
        },
      });
    }
  };

  return (
    <Styled.Wrapper viewOnly={viewOnly}>
      <Styled.LabelWrap>
        {window.$environment.CURRENT_LAYOUT !== 'SpireonNew' && (
          <Label className="logsInfoDisplay">
            {getLogsInfoDisplay(
              i18n,
              dailyLogDetails?.originalData?.dailyLog?.driverName,
              dailyLogDetails?.originalData?.dailyLog?.day
            )}
          </Label>
        )}
        <>
          {hasFetchDailyLogDetails && hasFetchDailyLogDetails.errorMessage ? (
            <Message type="error" message={i18n.t(hasFetchDailyLogDetails.errorMessage)} />
          ) : null}
        </>
      </Styled.LabelWrap>
      <Form
        type="server"
        direction="row"
        initialValues={{
          ...dailyLogDetails.finalData,
        }}
        RenderComponent={RenderComponent}
        handleOnSubmit={handlSubmit}
        customProps={{
          viewOnly: isDriver() ? true : viewOnly,
          tableEditBlocked: isDriver() ? true : tableEditBlocked,
          wholeLogEditBlocked: isDriver() ? false : wholeLogEditBlocked,
          coDriverEventReassignment,
          pendingDriverChange,
          logIdPub: id,
          createCoDriverEventReassignment,
          fetchDailyLogDetailsApiId,
          apiStatus,
          hasUpdateDailyLogDetails,
          yData,
          xData,
          setDailyLogEntries,
          assignedDriver: assignedDriver,
          Styled,
          history,
          i18n,
          setFinalListForUpdate,
          udEdit: udEdit || false,
          finalListForUpdate,
          drivers,
          providerMode,
          setProviderMode,
          getDailyLogDetails,
          originalData: dailyLogDetails.originalData.dailyLog,
          unformattedDriverList: dailyLogDetails.originalData.dailyLog.companyDrivers,
          enableEditOW: hasEnabledFeature('editOilFieldWaitingEnabled') && dailyLogDetails?.originalData?.enableEditOW === true,
          dailyLogDetails,
          udReassignment,
          companyDrivers: dailyLogDetails?.originalData?.dailyLog?.companyDrivers,
          logIdPub: id,
          createUdReassignment,
          apiStatus,
          fetchDailyLogDetailsApiId,
        }}
        handleOnClear={clearUpdateDailyLogDetails}
        validationSchema={schema}
      />
      {validationMessage && <Message type="error" message={validationMessage} noTrim />}
    </Styled.Wrapper>
  );
};

export default Component;
Component.defaultTypes = {};
Component.propTypes = {
  apiStatus: PropTypes.object.isRequired,
  manageLogList: PropTypes.object.isRequired,
  fetchManageLogList: PropTypes.func.isRequired,
  setSelectedManageLogItem: PropTypes.func.isRequired,
  saveDailyLogDetails: PropTypes.func.isRequired,
  getDailyLogDetails: PropTypes.func.isRequired,
  clearUpdateDailyLogDetails: PropTypes.func.isRequired,
  filters: PropTypes.object.isRequired,
  getFilters: PropTypes.func.isRequired,
};
